import { t } from "../../i18n/i18n";
import { authDate, authTime } from "../authText";

/** Fiche de remise d'un code (docs/17 §5.3, §8.3) : une par personne, découpable, imprimée par le navigateur
 *  (`window.print`) depuis un cadre caché qui porte sa propre feuille de style d'impression — aucune bibliothèque PDF,
 *  rien d'ajouté à la page de l'application. Le code ne vit que le temps de l'impression : le cadre est retiré
 *  ensuite, rien n'est rangé dans le navigateur. */

export interface Slip {
  name: string;
  login: string | null;
  code: string;
  expiresAt: string | null;
}

function escape(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

/** Texte d'une fiche, en lignes (§8.3, « Fiche de remise »). */
export function slipLines(slip: Slip, unit: string | null): { title: string; lines: string[] } {
  const title = unit ? `Generex Web · ${unit}` : "Generex Web";
  return {
    title,
    lines: [
      t("Compte de {nom}.", { nom: slip.name }),
      t("Identifiant : {identifiant}.", { identifiant: slip.login ?? "—" }),
      t("Code : {code}, valable jusqu'au {date} à {heure}, une seule fois.", {
        code: slip.code,
        date: authDate(slip.expiresAt),
        heure: authTime(slip.expiresAt),
      }),
      t("Sur un poste Generex : Connexion › « J'ai un code d'activation ou de réinitialisation », puis choisissez votre mot de passe."),
      t("Détruisez cette fiche après usage."),
    ],
  };
}

/** Document imprimé : les fiches à la suite, séparées par un pointillé de découpe, jamais coupées entre deux pages. */
export function slipsDocument(slips: Slip[], unit: string | null): string {
  const body = slips
    .map((slip) => {
      const { title, lines } = slipLines(slip, unit);
      const [who, login, code, ...rest] = lines;
      return `<section class="slip"><h1>${escape(title)}</h1><p class="who">${escape(who!)}</p><p>${escape(login!)}</p><p class="code">${escape(code!)}</p>${rest
        .map((line) => `<p>${escape(line)}</p>`)
        .join("")}</section>`;
    })
    .join("");
  // Noir sur blanc, polices du système : la fiche se lit et se photocopie partout.
  const style = [
    "@page{margin:14mm}",
    "body{font:11pt/1.45 system-ui,-apple-system,'Segoe UI',sans-serif;color:black;background:white;margin:0}",
    ".slip{border-bottom:1px dashed gray;padding:10mm 2mm;break-inside:avoid;page-break-inside:avoid}",
    ".slip:last-child{border-bottom:0}",
    "h1{font-size:10pt;font-weight:600;margin:0 0 3mm;color:dimgray}",
    "p{margin:0 0 1.5mm}",
    ".who{font-size:13pt;font-weight:600}",
    ".code{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:13pt;letter-spacing:.06em}",
  ].join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escape(t("Fiches de remise"))}</title><style>${style}</style></head><body>${body}</body></html>`;
}

/** Imprime les fiches depuis un cadre caché, retiré après l'impression (ou au bout d'une minute). */
export function printSlips(slips: Slip[], unit: string | null): void {
  if (!slips.length || typeof document === "undefined") return;
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  Object.assign(frame.style, { position: "absolute", width: "0", height: "0", border: "0", left: "-9999px" });
  let removed = false;
  const remove = () => {
    if (removed) return;
    removed = true;
    frame.remove();
  };
  frame.onload = () => {
    const view = frame.contentWindow;
    if (!view) return remove();
    view.addEventListener("afterprint", () => window.setTimeout(remove, 0));
    view.focus();
    view.print();
    window.setTimeout(remove, 60_000);
  };
  frame.srcdoc = slipsDocument(slips, unit);
  document.body.appendChild(frame);
}
